import React, { useEffect, useRef, useState, useContext } from 'react';
import * as styles from './CareerBanner.module.scss';
import { gsap } from 'gsap';
import bannerImageLargeDesktop from './images/career-banner-large-desktop.jpg';
import bannerImageSmallDesktop from './images/career-banner-small-desktop.jpg';
import bannerImageTablet from './images/career-banner-tablet.jpg';
import bannerImageMobile from './images/career-banner-mobile.jpg';
import { headerWithCareerBanner } from '../../utils/headerWithCareerBanner';
import { TabFilters } from '../../AppLanding';

const { __ } = window.wp.i18n;

interface SearchComponentProps {
  currentKeyword: string;
  onSearch: (newKeyword: string) => void;
  onTabFilterOpen: (isOpen: boolean) => void;
  results: (isVisible: boolean) => void;
}

// get the filters selected in settings
interface Window {
  cg_jobs_banner_strikeout_text?: string;
  cg_jobs_banner_handwritten_text?: string;
  cg_jobs_placeholder_text?: string;
  cg_jobs_banner_image_large_desktop?: string;
  cg_jobs_banner_image_small_desktop?: string;
  cg_jobs_banner_image_tablet?: string;
  cg_jobs_banner_image_mobile?: string;
  cg_jobs_explore_all_jobs_button_text?: string;
  cg_jobs_explore_all_jobs_url?: string;
}
const bannerStrikeOutText = (window as Window).cg_jobs_banner_strikeout_text;
const bannerHandWrittenText = (window as Window).cg_jobs_banner_handwritten_text;
const placeholderText = (window as Window).cg_jobs_placeholder_text;
const bannerbgImageLargeDesktop = (window as Window).cg_jobs_banner_image_large_desktop;
const bannerbgImageSmallDesktop = (window as Window).cg_jobs_banner_image_small_desktop;
const bannerbgImageTablet = (window as Window).cg_jobs_banner_image_tablet;
const bannerbgImageMobile = (window as Window).cg_jobs_banner_image_mobile;
const exploreAllJobsButtonText = (window as Window).cg_jobs_explore_all_jobs_button_text;
const exploreAllJobsLink = (window as Window).cg_jobs_explore_all_jobs_url;


export const CareerBanner: React.FC<SearchComponentProps> = ({ currentKeyword, onSearch, onTabFilterOpen, results }) => {
  const [keyword, setKeyword] = useState(currentKeyword);
  //const [isFilterClose, setIsFilterClose] = useState(true);
  const [placeholder, setPlaceholder] = useState(placeholderText ? placeholderText : `${__('Search jobs by location, profession or keywords', 'cg-jobs')}`);
  const [isMobileScreen, setIsMobileScreen] = useState<boolean>(window.matchMedia("(max-width: 991px)").matches);
  const [isTabletScreen, setIsTabletScreen] = useState<boolean>(window.matchMedia("(max-width: 1200px)").matches);
  const inputRef = useRef<HTMLInputElement>(null);
  const tabFilters = useContext(TabFilters);

  const textRef = useRef<HTMLDivElement>(null);
  const svgRef = useRef<SVGPathElement>(null);
  const svgRefElement = useRef<SVGSVGElement>(null);
  const textRef2 = useRef<HTMLDivElement>(null);

  // Update `keyword` with `currentKeyword` prop
  useEffect(() => {
    setKeyword(currentKeyword);
  }, [currentKeyword]);

  // Effect to trigger API callback when inputValue is empty
  useEffect(() => {
    if (keyword === '') {
      onSearch('');
    }
  }, [keyword]);

  useEffect(() => {
    const changeToMobileLayout = () => {
      const wrapper = document.querySelector('#wrapper-cjs') as HTMLElement;

      // For mobile
      if (window.matchMedia("(min-width: 992px").matches) {
        setIsMobileScreen(false);
        setPlaceholder(placeholderText ? placeholderText : `${__('Search jobs by location, profession or keywords', 'cg-jobs')}`);
        if (wrapper) wrapper.removeAttribute('style');
      } else {
        setIsMobileScreen(true);
        setPlaceholder(placeholderText ? placeholderText : `${__('Search for jobs', 'cg-jobs')}`);
        if (wrapper) {
          if (!tabFilters) {
            wrapper.style.gridTemplateColumns = '1fr';
            //setIsFilterClose(true);                
          } else {
            wrapper.removeAttribute('style');
          }
        }
      }

      // For tablet
      if (window.matchMedia("(min-width: 1200px").matches) {
        setIsTabletScreen(false);
        //setIsFilterClose(true);
      } else {
        setIsTabletScreen(true);
      }

      headerWithCareerBanner();
    };

    // Set the initial placeholder
    changeToMobileLayout();

    // Add an event listener for window resize
    window.addEventListener('resize', changeToMobileLayout);

    // Cleanup event listener on component unmount
    return () => window.removeEventListener('resize', changeToMobileLayout);
  }, []);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    if (keyword.length >= 3) {
      onSearch((keyword.trim()));
      results(true);
    }
    e.preventDefault();
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    const inputElement = e.target as HTMLInputElement;
    const value = inputElement.value;
    // Check if the Enter key was pressed
    if (e.key === 'Enter' && value.length >= 3) {
      inputRef.current && onSearch((inputRef.current.value.trim()));
      e.preventDefault();
      // Blur the input field
      if (inputRef.current) {
        inputRef.current.blur();
      }
    }

    if (value.length >= 3) results(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setKeyword(decodeURIComponent(value));

    // Trigger search if 3 or more letters are typed
    if (value.length >= 3) {
      onSearch((value.trim()));
      results(true);
    } else if (value.length === 0) {
      onSearch(''); // Clear search on empty input
      results(false);
    }
  };

  // 
  const OpenMobileFilters = () => {
    const filters = document.querySelector('.open-filters') as HTMLElement;
    filters ? filters.style.display = "block" : "";

    // Make sure modal scrolls to top when opened
    const modal = document.getElementById('offcanvasBottom');
    const modalBody = modal && modal.querySelector('.offcanvas-body');
    if (modalBody) {
      modalBody.scrollTop = 0;   // Scroll the modal body to the top
    }
  }

  const OpenTabletFilters = () => {
    const filterWrapper = document.querySelector('#filter-wrapper') as HTMLElement;
    const filterContainer = document.querySelector('#filter-container') as HTMLElement;
    const wrapper = document.querySelector('#wrapper-cjs') as HTMLElement;

    if (!tabFilters) {
      filterWrapper.style.transform = 'translateX(0)';
      filterWrapper.style.opacity = '1';
      filterContainer.style.display = 'block';
      wrapper.style.gridTemplateColumns = '2.99fr 1.01fr';
    } else {
      filterWrapper.style.transform = 'translateX(100%)';
      filterWrapper.style.opacity = '0';
      filterContainer.style.display = 'none';
      wrapper.style.gridTemplateColumns = '1fr';
    }
    //setIsFilterClose(!isFilterClose);
    onTabFilterOpen(!tabFilters);
  }

  // To Animate text
  useEffect(() => {
    const animateText = () => {
      if (textRef.current) textRef.current.style.opacity = '0';
      if (svgRefElement.current) svgRefElement.current.style.opacity = '0';

      // Animation for the handwritten text
      if (textRef.current) {
        const text = textRef.current;
        const textContent = text.innerText;

        //Hide the original text and create an empty span for each letter
        text.innerHTML = '';
        textContent.split('').forEach((char, index) => {
          const span = document.createElement('span');
          span.innerText = char;
          text.appendChild(span);
        });

        //Animate the text
        gsap.delayedCall(1, () => {
          gsap.fromTo(
            text,
            {
              opacity: 0,
            },
            {
              opacity: 1,
              duration: 1,
            }
          );
        })


        // After the text is written, draw the line-through SVG path
        gsap.delayedCall(1.5, () => {
          if (svgRef.current) {
            svgRefElement.current && (svgRefElement.current.style.opacity = '1');
            gsap.to(svgRef.current, {
              strokeDashoffset: 0,
              duration: 2,
              ease: 'power2.out',
            });
          }
        });
      }

      if (textRef2.current) {
        const text = textRef2.current;
        const textContent = text.innerText;

        // Hide the original text and create an empty span for each letter
        text.innerHTML = '';
        textContent.split('').forEach((char, index) => {
          const span = document.createElement('span');
          span.innerText = char;
          text.appendChild(span);
        });

        // Animate the text
        gsap.delayedCall(2, () => {
          text.style.opacity = '1';
          gsap.fromTo(
            text.querySelectorAll('span'),
            {
              opacity: 0,
              x: -10,
            },
            {
              opacity: 1,
              x: 0,
              stagger: 0.1, // Stagger the animation for each letter
              duration: 0.2,
            }
          );
        });
      }
    };

    // Run animation on mount
    animateText();

    // Run animation on resize
    const handleResize = () => {
      animateText();
    };

    // Add resize event listener
    window.addEventListener('resize', handleResize);

    // Cleanup the resize event listener
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const content = document.querySelector('#cg-jobs-search-root-landing') as HTMLElement;
  if (content) content.style.overflowX = 'clip'; content.style.backgroundColor='var(--zodiak-surface-fog-lite)';

  // Banner Images from settings or default images
  const heroLargeDesktopImgURL = bannerbgImageLargeDesktop ? bannerbgImageLargeDesktop : bannerImageLargeDesktop;
  const heroSmallDesktopImgURL = bannerbgImageSmallDesktop ? bannerbgImageSmallDesktop : bannerImageSmallDesktop;
  const heroTabImgURL = bannerbgImageTablet ? bannerbgImageTablet : bannerImageTablet;
  const heroMobImgURL = bannerbgImageMobile ? bannerbgImageMobile : bannerImageMobile;

  return (
    <>
      <div className={styles['career-banner']} id="career-banner">
        <div className={styles['banner-image-wrapper']}>
          <picture>
            <source srcSet={`${heroLargeDesktopImgURL}?w=2880&quality=70 1x, ${heroLargeDesktopImgURL}?w=2880&quality=70 2x`} media="(min-width: 1728px)" />
            <source srcSet={`${heroSmallDesktopImgURL}?w=1728&quality=70 1x, ${heroSmallDesktopImgURL}?w=2880&quality=70 2x`} media="(min-width: 1200px)" />
            <source srcSet={`${heroTabImgURL}?w=1024&quality=70 1x, ${heroTabImgURL}?w=1536&quality=70 2x`} media="(min-width: 992px)" />
            <source srcSet={`${heroMobImgURL}?w=430&quality=70 1x, ${heroMobImgURL}?w=1152&quality=70 2x`} media="(min-width: 0)" />
            <img width={'100%'} height={'100%'} className="lazy" src={heroLargeDesktopImgURL} data-sizes="auto" loading="lazy" alt="" />
          </picture>
        </div>

        <div className={[styles['banner-content'], styles['container-cjs'], 'container'].join(" ")}>
          {isMobileScreen ?
            <div className={styles['rewrite-mobile']}>
              {bannerHandWrittenText ? bannerHandWrittenText : "Rewrite your future"}
            </div>
            : <>
              <h1>
                <div style={{ position: 'relative', display: 'inline-block', textAlign: 'left' }}>
                  <div className={styles['line-through-text']} ref={textRef}>
                    {bannerStrikeOutText ? bannerStrikeOutText : "Wait for change"}
                  </div>

                  {/* SVG line-through animation */}
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 500 100"
                    className={styles['svgLine']}
                    ref={svgRefElement}
                  >
                    <path
                      ref={svgRef}
                      //d="M0,25 C150,10 200,40 250,25 C300,10 350,40 380,25"
                      d="M0,20 C150,15 200,25 250,25 C300,18 350,25 390,25"
                      fill="transparent"
                      stroke="white"
                      strokeWidth="3"
                      strokeDasharray="800"
                      strokeDashoffset="800"
                    />
                  </svg>

                  <div className={styles['rewrite']} ref={textRef2} style={{}}>
                    {bannerHandWrittenText ? bannerHandWrittenText : "Rewrite your future"}
                  </div>
                </div>
              </h1>
              <a href={exploreAllJobsLink} className={styles['explore-link']}>{exploreAllJobsButtonText ? exploreAllJobsButtonText : 'Explore all jobs'}</a>
              <div className={styles['search-container']}>
                <form onSubmit={(e) => handleSubmit(e)}>
                  <input
                    type="text"
                    value={keyword}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    onFocus={(e) => {
                      setPlaceholder("");
                     // e.target.select();
                    }}
                    onBlur={() => setPlaceholder(placeholderText ? placeholderText : `${__('Search jobs by location, profession or keywords', 'cg-jobs')}`)}
                    placeholder={placeholder}
                    aria-label={placeholder}
                  />
                  <button type="submit">{__('Search', 'cg-jobs')}</button>
                </form>
              </div>
            </>
          }
        </div>
      </div>
      {isTabletScreen &&
        <div className={styles['mobile-container']}>
          <div className={styles['search-container']}>
            <form onSubmit={(e) => handleSubmit(e)}>
              <input
                type="text"
                ref={inputRef}
                value={keyword}
                onChange={handleChange}
                onFocus={(e) => {
                  setPlaceholder("");
                  //e.target.select();
                }}
                onKeyDown={handleKeyDown}
                onBlur={() => setPlaceholder(placeholderText ? placeholderText : `${__('Search for jobs', 'cg-jobs')}`)}
                placeholder={placeholder}
                aria-label={placeholder}
              />
            </form>
            {isMobileScreen ?
              <button type='button' data-bs-toggle="offcanvas" data-bs-target="#offcanvasBottom" aria-controls="offcanvasBottom" className={styles['open-mobile-filter']} onClick={OpenMobileFilters} aria-label='filter toggle' disabled={keyword.length === 0}>
                {/*<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M14 18H21M3 18H5M5 18C5 19.3807 6.11929 20.5 7.5 20.5C8.88071 20.5 10 19.3807 10 18C10 16.6193 8.88071 15.5 7.5 15.5C6.11929 15.5 5 16.6193 5 18ZM20 12H21M3 12H10M13 6H21M13 6C13 4.61929 11.8807 3.5 10.5 3.5C9.11929 3.5 8 4.61929 8 6C8 7.38071 9.11929 8.5 10.5 8.5C11.8807 8.5 13 7.38071 13 6ZM3 6H4M16.5 14.5C15.1193 14.5 14 13.3807 14 12C14 10.6193 15.1193 9.5 16.5 9.5C17.8807 9.5 19 10.6193 19 12C19 13.3807 17.8807 14.5 16.5 14.5Z" stroke="white" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                </svg>*/}

                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                  <path d="M12 16H19M1 16H3M3 16C3 17.3807 4.11929 18.5 5.5 18.5C6.88071 18.5 8 17.3807 8 16C8 14.6193 6.88071 13.5 5.5 13.5C4.11929 13.5 3 14.6193 3 16ZM18 10H19M1 10H8M11 4H19M11 4C11 2.61929 9.88071 1.5 8.5 1.5C7.11929 1.5 6 2.61929 6 4C6 5.38071 7.11929 6.5 8.5 6.5C9.88071 6.5 11 5.38071 11 4ZM1 4H2M14.5 12.5C13.1193 12.5 12 11.3807 12 10C12 8.61929 13.1193 7.5 14.5 7.5C15.8807 7.5 17 8.61929 17 10C17 11.3807 15.8807 12.5 14.5 12.5Z" stroke="var(--zodiak-text-inverse)" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                </svg>
              </button>
              :
              <button type='button' className={styles['open-filter']} onClick={OpenTabletFilters} disabled={keyword.length === 0}>
                {!tabFilters ?
                  <>
                    {/*<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M14 18H21M3 18H5M5 18C5 19.3807 6.11929 20.5 7.5 20.5C8.88071 20.5 10 19.3807 10 18C10 16.6193 8.88071 15.5 7.5 15.5C6.11929 15.5 5 16.6193 5 18ZM20 12H21M3 12H10M13 6H21M13 6C13 4.61929 11.8807 3.5 10.5 3.5C9.11929 3.5 8 4.61929 8 6C8 7.38071 9.11929 8.5 10.5 8.5C11.8807 8.5 13 7.38071 13 6ZM3 6H4M16.5 14.5C15.1193 14.5 14 13.3807 14 12C14 10.6193 15.1193 9.5 16.5 9.5C17.8807 9.5 19 10.6193 19 12C19 13.3807 17.8807 14.5 16.5 14.5Z" stroke="white" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                    </svg> */}
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                      <path d="M12 16H19M1 16H3M3 16C3 17.3807 4.11929 18.5 5.5 18.5C6.88071 18.5 8 17.3807 8 16C8 14.6193 6.88071 13.5 5.5 13.5C4.11929 13.5 3 14.6193 3 16ZM18 10H19M1 10H8M11 4H19M11 4C11 2.61929 9.88071 1.5 8.5 1.5C7.11929 1.5 6 2.61929 6 4C6 5.38071 7.11929 6.5 8.5 6.5C9.88071 6.5 11 5.38071 11 4ZM1 4H2M14.5 12.5C13.1193 12.5 12 11.3807 12 10C12 8.61929 13.1193 7.5 14.5 7.5C15.8807 7.5 17 8.61929 17 10C17 11.3807 15.8807 12.5 14.5 12.5Z" stroke="var(--zodiak-text-inverse)" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>{__('Filter', 'cg-jobs')}
                  </>
                  :
                  <>
                    {/*<svg width="11" height="11" viewBox="0 0 14 15" fill="none" className={styles['close-icon']} xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.7201 13.0677L1.06445 1.65234" stroke="white" stroke-width="2.0373" stroke-linecap="round" stroke-linejoin="round" />
                      <path d="M1.06445 13.0677L12.7201 1.65234" stroke="white" stroke-width="2.0373" stroke-linecap="round" stroke-linejoin="round" />
                    </svg> */}
                    <svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32" fill="none" className={styles['close-icon']}>
                      <path d="M24 24L16 16M16 16L8 8M16 16L24 8M16 16L8 24" stroke="var(--zodiak-text-inverse)" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" />
                    </svg>
                    {__('Close Filter', 'cg-jobs')}
                  </>
                }
              </button>
            }
          </div>
        </div>
      }
    </>
  )
}